import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";

import {
  addAllowedDomain,
  listAllowedDomains,
  removeAllowedDomain,
  type AdminAllowedDomainRow,
} from "../../../lib/admin";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";
import { SectionHeader } from "../../../components/aspects/SectionHeader";

const THEME = PANE_THEMES.Admin;

export default function AdminDomainsScreen() {
  const [rows, setRows] = useState<AdminAllowedDomainRow[]>([]);
  const [domain, setDomain] = useState("");
  const [universityName, setUniversityName] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [armedRemove, setArmedRemove] = useState<string | null>(null);
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setRows(await listAllowedDomains());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load domains");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  useEffect(
    () => () => {
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    },
    []
  );

  async function onAdd() {
    if (!domain.trim() || !universityName.trim()) return;
    setAdding(true);
    setErr(null);
    try {
      await addAllowedDomain(domain, universityName);
      setDomain("");
      setUniversityName("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add domain");
    } finally {
      setAdding(false);
    }
  }

  async function onRemove(d: string) {
    if (armedRemove !== d) {
      setArmedRemove(d);
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      armedTimerRef.current = setTimeout(() => setArmedRemove(null), 4000);
      return;
    }
    if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    setArmedRemove(null);
    try {
      await removeAllowedDomain(d);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to remove domain");
    }
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 880 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Platform · Domains"
          title="Email domains"
          subtitle="Universities allowed to sign up — students with a matching email domain land directly on the student portal."
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        {/* Add form */}
        <View style={{ padding: 16, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
          <SectionHeader theme={THEME} title="Add domain" tone="dark" />
          <View style={{ gap: 10 }}>
            <AdminInput theme={THEME} value={domain} onChangeText={setDomain} placeholder="example.edu" autoCapitalize="none" />
            <AdminInput theme={THEME} value={universityName} onChangeText={setUniversityName} placeholder="Example University" />
          </View>
          <View style={{ marginTop: 12 }}>
            <AdminButton theme={THEME} label="Add domain" loading={adding} disabled={!domain.trim() || !universityName.trim()} onPress={() => void onAdd()} />
          </View>
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading domains…" />
          ) : rows.length === 0 ? (
            <AdminEmpty theme={THEME} title="No domains yet." body="Add a school's domain above to start letting their students through." />
          ) : (
            <ListRowGroup theme={THEME}>
              {rows.map((row, i) => {
                const armed = armedRemove === row.domain;
                return (
                  <ListRow
                    key={row.domain}
                    theme={THEME}
                    first={i === 0}
                    title={row.domain}
                    subtitle={row.university_name}
                    trailing={
                      <AdminButton
                        theme={THEME}
                        label={armed ? "Click again" : "Remove"}
                        variant={armed ? "destructive" : "outline"}
                        size="sm"
                        onPress={() => void onRemove(row.domain)}
                      />
                    }
                  />
                );
              })}
            </ListRowGroup>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
